import { useSearchParams } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import { LeadForm } from "@/components/site/LeadForm";
import { PageBanner } from "@/components/site/PageBanner";
import { mailHref, telHref, useSiteConfig } from "@/lib/site-config";
import { useSeo } from "@/hooks/useSeo";

type ContactIntent = "buy" | "rent" | "sell" | "let" | "off-plan" | "valuation";

const VALID_INTENTS = new Set<ContactIntent>([
  "buy",
  "rent",
  "sell",
  "let",
  "off-plan",
  "valuation",
]);

export function ContactPage() {
  const siteConfig = useSiteConfig();
  const [searchParams] = useSearchParams();
  const intentParam = searchParams.get("intent") ?? "";
  const defaultIntent = VALID_INTENTS.has(intentParam as ContactIntent)
    ? (intentParam as ContactIntent)
    : "";
  const hasAddress =
    siteConfig.addressLine1.set || siteConfig.addressLine2.set;
  // Precise place query, building name first — joining the postal
  // address lines makes the Maps embed fuzzy-match every Binghatti
  // tower in JVC instead of pinning the office itself.
  const mapQuery = "Binghatti Emerald, Jumeirah Village Circle, Dubai";
  const directionsHref = mapQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}`
    : null;

  useSeo({
    title:
      "Contact Hive Homes | Dubai Property Advisors — Call, WhatsApp or Visit",
    description:
      "Get in touch with Hive Homes Dubai. Call, WhatsApp or email our property advisors. Book a viewing, request a valuation, or ask about any community. We respond within 24 hours.",
  });

  return (
    <>
      {/* Header holds just the page title, per the client's feedback;
          the "Get in Touch" headline and response-time copy live in the
          column below instead. The client banner photo sits behind it,
          matching the Properties / Insights / Communities heroes. */}
      <PageBanner
        image="/images/site/contact.jpg"
        alt="Hive Homes brand folder and business cards on a marble desk in front of the Dubai skyline"
      >
        <SectionHeading as="h1" size="xl" title="Contact Us." />
      </PageBanner>

      <section className="container-wide grid gap-12 py-12 md:grid-cols-12 md:gap-14 md:py-16 short:py-8">
        {/* Contact details + map */}
        <div className="md:col-span-5">
          <h2
            className="font-display text-[24px] leading-snug text-[var(--text)] sm:text-[30px] md:text-[36px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            Get in Touch.
          </h2>
          <p className="mt-4 max-w-[52ch] text-[14.5px] leading-[1.7] text-[var(--text-soft)] sm:text-[15px]">
            Our team responds to every enquiry within 24 hours. For a faster
            response, message us directly on WhatsApp — we are usually
            available the same day.
          </p>

          <dl className="mt-10 grid gap-5 border-t border-[var(--line)] pt-8">
            {siteConfig.phone.set && (
              <ContactRow
                label="Phone"
                value={siteConfig.phone.display}
                href={telHref(siteConfig.phone)}
              />
            )}
            {siteConfig.whatsapp.set && (
              <ContactRow
                label="Mobile"
                value={siteConfig.whatsapp.display}
                href={telHref(siteConfig.whatsapp)}
              />
            )}
            {siteConfig.email.set && (
              <ContactRow
                label="Email"
                value={siteConfig.email.display}
                href={mailHref(siteConfig.email)}
              />
            )}
            {siteConfig.hours.set && (
              <ContactRow label="Office Hours" value={siteConfig.hours.display} />
            )}
          </dl>

          {mapQuery && (
            <div className="mt-10">
              <p className="eyebrow-bare text-[var(--text-muted)]">Find Us</p>
              {hasAddress && (
                <p className="mt-3 text-[15px] leading-[1.6] text-[var(--text)]">
                  {[siteConfig.addressLine1.raw, siteConfig.addressLine2.raw]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
              <div className="mt-4 aspect-[4/3] overflow-hidden border border-[var(--line-strong)]">
                <iframe
                  title="Hive Homes office"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                  className="h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              {directionsHref && (
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-arrow mt-4"
                >
                  Get Directions
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2 7h10m-4-4 4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Lead form */}
        <div className="md:col-span-7 md:pl-4">
          <h2
            className="font-display text-[24px] leading-[1.1] text-[var(--text)] sm:text-[30px] sm:leading-[1.05] md:text-[38px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            Tell us what you're looking for.
          </h2>
          <div className="mt-7 border border-[var(--line-strong)] bg-[var(--surface)] p-5 shadow-[0_22px_70px_-52px_rgba(20,30,48,0.42)] sm:mt-8 sm:p-7">
            <LeadForm
              source="contact"
              cta="Send Enquiry"
              successMessage="Thank you — one of our advisors will be in touch within 24 hours."
              defaultIntent={defaultIntent}
            />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) {
  const content = (
    <>
      <dt className="eyebrow-bare text-[var(--text-muted)]">{label}</dt>
      <dd className="mt-1.5 text-[16px] text-[var(--text)]">{value}</dd>
    </>
  );
  return (
    <div className="border-b border-[var(--line)] pb-5">
      {href && href !== "#" ? (
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel="noopener noreferrer"
          className="block transition-opacity hover:opacity-70"
        >
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  );
}
